<template>
  <div>
    <div class="modal fade" id="sendNotatkaModal" ref="sendNotatkaModalEle" tabindex="-1" role="dialog"
      aria-labelledby="sendNotatkaModalTitle" aria-hidden="true">
      <div class="modal-dialog" :class="productViewMode === 'details' ? 'modal-xl' : 'modal-full-width'"
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="sendNotatkaModalTitle">
              Wyślij notatkę
            </h5>
            <button type="button" class="btn-close" aria-label="Zamknij" @click="hide"></button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="sendNote">
              <div class="mb-3">
                <label class="form-label" for="emailInput">
                  Wyślij notatkę do:
                </label>

                <div class="email-tags-container form-control" @click="$refs.emailInput.focus()">
                  <span v-for="(email, index) in recipients" :key="email" class="email-tag">
                    {{ email }}
                    <button type="button" class="email-tag-remove" aria-label="Usuń adres"
                      @click.stop="removeRecipient(index)">
                      &times;
                    </button>
                  </span>

                  <input id="emailInput" ref="emailInput" v-model="emailInput" type="text" class="email-tag-input"
                    placeholder="Wpisz e-mail i naciśnij Enter" @keydown.enter.prevent="addRecipient"
                    @keydown="handleRecipientKeydown" @blur="addRecipient" />
                </div>

                <small class="text-muted">
                  Adresy oddzielaj Enterem, przecinkiem lub średnikiem.
                </small>

                <div v-if="emailError" class="text-danger small mt-1">
                  {{ emailError }}
                </div>
              </div>

              <div class="mb-3">
                <label for="noteSubject" class="form-label">
                  Temat wiadomości
                </label>

                <input id="noteSubject" v-model="subject" type="text" class="form-control" required />
              </div>

              <div class="mb-3">
                <label class="form-label">
                  Treść wiadomości — podsumowanie spotkania
                </label>

                <div class="note-preview">
                  <div v-if="selectedNoteRows.length" class="table-responsive">
                    <table class="table table-bordered table-striped table-sm">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Temat</th>
                          <th>Osoba odpowiedzialna</th>
                          <th>Kroki podjęte do rozwiązania problemu</th>
                          <th>Dodatkowe punkty</th>
                          <th>Data spotkania</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr v-for="(row, index) in selectedNoteRows" :key="row.id ?? index">
                          <td>{{ index + 1 }}</td>
                          <td>{{ row.topic }}</td>
                          <td>{{ row.person_responsible_for }}</td>
                          <td>{{ row.resolution_steps }}</td>
                          <td>{{ row.additional_points }}</td>
                          <td>{{ formatDate(row.meeting_date) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else class="alert alert-warning">
                    Nie zaznaczono żadnych rekordów.
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">
                  Wymagani uczestnicy
                </label>

                <div class="participant-select">
                  <div class="participant-tags">
                    <span v-for="(person, index) in requiredParticipants" :key="participantKey(person)"
                      class="participant-tag">
                      {{ person.name }}
                      <small v-if="person.email">
                        ({{ person.email }})
                      </small>

                      <button type="button" aria-label="Usuń uczestnika" @click="removeParticipant('required', index)">
                        &times;
                      </button>
                    </span>

                    <input v-model="participantSearch.required" type="text" class="participant-search"
                      placeholder="Szukaj po imieniu, nazwisku lub e-mailu..." autocomplete="off"
                      @focus="activeParticipantSearch = 'required'" @keydown.enter.prevent="addParticipant('required')"
                      @keydown.esc="activeParticipantSearch = null" />
                  </div>

                  <div v-if="
                    activeParticipantSearch === 'required' &&
                    participantSearch.required.trim()
                  " class="participant-dropdown">
                    <button v-for="user in filteredUsers('required')" :key="user.id" type="button"
                      class="participant-option" @mousedown.prevent="addParticipant('required', user)">
                      <strong>
                        {{ user.last_name }} {{ user.first_name }}
                      </strong>
                      <small>{{ user.email }}</small>
                    </button>

                    <button v-if="!hasExactParticipantMatch('required')" type="button"
                      class="participant-option participant-manual" @mousedown.prevent="addParticipant('required')">
                      <strong>
                        + Dodaj ręcznie:
                        {{ participantSearch.required }}
                      </strong>
                      <small>
                        Dodaj wpisaną nazwę lub adres e-mail
                      </small>
                    </button>

                    <div v-if="
                      !filteredUsers('required').length &&
                      hasExactParticipantMatch('required')
                    " class="participant-empty">
                      Ta osoba jest już dodana.
                    </div>
                  </div>
                </div>

                <small class="text-muted">
                  Wpisz fragment nazwiska, imienia lub e-maila i wybierz osobę.
                  Enter dodaje wpis ręczny, jeśli nie wybierzesz podpowiedzi.
                </small>
              </div>

              <div class="mb-3">
                <label class="form-label">
                  Opcjonalni uczestnicy
                </label>

                <div class="participant-select">
                  <div class="participant-tags">
                    <span v-for="(person, index) in optionalParticipants" :key="participantKey(person)"
                      class="participant-tag">
                      {{ person.name }}
                      <small v-if="person.email">
                        ({{ person.email }})
                      </small>

                      <button type="button" aria-label="Usuń uczestnika" @click="removeParticipant('optional', index)">
                        &times;
                      </button>
                    </span>

                    <input v-model="participantSearch.optional" type="text" class="participant-search"
                      placeholder="Szukaj po imieniu, nazwisku lub e-mailu..." autocomplete="off"
                      @focus="activeParticipantSearch = 'optional'" @keydown.enter.prevent="addParticipant('optional')"
                      @keydown.esc="activeParticipantSearch = null" />
                  </div>

                  <div v-if="
                    activeParticipantSearch === 'optional' &&
                    participantSearch.optional.trim()
                  " class="participant-dropdown">
                    <button v-for="user in filteredUsers('optional')" :key="user.id" type="button"
                      class="participant-option" @mousedown.prevent="addParticipant('optional', user)">
                      <strong>
                        {{ user.last_name }} {{ user.first_name }}
                      </strong>
                      <small>{{ user.email }}</small>
                    </button>

                    <button v-if="!hasExactParticipantMatch('optional')" type="button"
                      class="participant-option participant-manual" @mousedown.prevent="addParticipant('optional')">
                      <strong>
                        + Dodaj ręcznie:
                        {{ participantSearch.optional }}
                      </strong>
                      <small>
                        Dodaj wpisaną nazwę lub adres e-mail
                      </small>
                    </button>

                    <div v-if="
                      !filteredUsers('optional').length &&
                      hasExactParticipantMatch('optional')
                    " class="participant-empty">
                      Ta osoba jest już dodana.
                    </div>
                  </div>
                </div>

                <small class="text-muted">
                  Możesz wybrać użytkownika z listy lub wpisać uczestnika
                  spoza listy.
                </small>

                <div class="row g-3 mb-3">
                  <div class="col-md-6">
                    <label for="meetingDateTime" class="form-label">
                      Data i godzina spotkania
                    </label>

                    <input id="meetingDateTime" v-model="meetingDateTime" type="datetime-local" class="form-control"
                      required />
                  </div>

                  <div class="col-md-6">
                    <label for="meetingLocation" class="form-label">
                      Lokalizacja
                    </label>

                    <input id="meetingLocation" v-model="meetingLocation" type="text" class="form-control"
                      placeholder="Podaj lokalizację" maxlength="255" required />
                  </div>
                </div>

				<div class="mb-3">
				  <label for="meetingSummary" class="form-label">
					Podsumowanie spotkania
				  </label>

				  <textarea
					id="meetingSummary"
					v-model="meetingSummary"
					class="form-control"
					rows="5"
					maxlength="10000"
					placeholder="Wpisz podsumowanie spotkania..."
				  ></textarea>
				</div>


              </div>
            </form>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="hide">
              Zamknij
            </button>

            <button type="button" class="btn btn-primary" :disabled="recipients.length === 0 ||
              selectedNoteRows.length === 0 ||
              !subject.trim()
              " @click="sendNote">
              Wyślij notatkę
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import axios from "axios";

export default {
  name: "SendNotatkaModal",

  props: {
    project: {
      type: [Object, Number, String],
      default: null
    },
    users: {
      type: Array,
      default: () => []
    },
    users_list: {
      type: Array,
      default: () => []
    },
    projectFiles: {
      type: [Array, Object],
      default: () => []
    },
    selectedNoteRows: {
      type: Array,
      default: () => []
    }
  },


  data() {
    const now = new Date();
    const localNow = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    ).toISOString().slice(0, 16);

    return {
      modalObj: null,
	  meetingSummary: "",
      recipients: [],
      emailInput: "",
      emailError: "",
      requiredParticipants: [],
      optionalParticipants: [],
      participantSearch: {
        required: "",
        optional: ""
      },
      activeParticipantSearch: null,
      meetingDateTime: localNow,
      meetingLocation: "Ul. Nowy Dwór, 42-793 Ciasna",
      subject: `Projekt ${this.project?.number ?? ""} - podsumowanie spotkania`
    };
  },


  computed: {
    availableUsers() {
      return (this.users_list || [])
        .filter(user => user && user.id != null)
        .slice()
        .sort((a, b) => {
          const nameA = `${a.last_name || ""} ${a.first_name || ""}`;
          const nameB = `${b.last_name || ""} ${b.first_name || ""}`;

          return nameA.localeCompare(nameB, "pl");
        });
    }
  },

  watch: {
    selectedNoteRows: {
      immediate: true,
      handler(rows) {
        (rows || []).forEach(row => {
          const value = String(
            row.person_responsible_for || ""
          ).trim();

          if (
            this.isValidEmail(value) &&
            !this.recipients.some(
              email => email.toLowerCase() === value.toLowerCase()
            )
          ) {
            this.recipients.push(value);
          }
        });
      }
    }
  },

  mounted() {
    this.modalObj = new Modal(this.$refs.sendNotatkaModalEle);
    this.modalObj.show();

    this.$refs.sendNotatkaModalEle.addEventListener(
      "hidden.bs.modal",
      this.onModalHidden
    );
  },

  beforeUnmount() {
    if (this.$refs.sendNotatkaModalEle) {
      this.$refs.sendNotatkaModalEle.removeEventListener(
        "hidden.bs.modal",
        this.onModalHidden
      );
    }

    if (this.modalObj) {
      this.modalObj.dispose();
      this.modalObj = null;
    }
  },

  methods: {
    isValidEmail(email) {
      return /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(email);
    },

    handleRecipientKeydown(event) {
      if (
        event.key === "Backspace" &&
        !this.emailInput &&
        this.recipients.length
      ) {
        this.recipients.pop();
      }

      if (event.key === "," || event.key === ";") {
        event.preventDefault();
        this.addRecipient();
      }
    },

    addRecipient() {
      const values = this.emailInput
        .split(/[;,\s]+/)
        .map(email => email.trim())
        .filter(Boolean);

      if (!values.length) {
        this.emailInput = "";
        return;
      }

      const invalidEmails = [];

      values.forEach(email => {
        if (!this.isValidEmail(email)) {
          invalidEmails.push(email);
          return;
        }

        const normalized = email.toLowerCase();

        if (
          !this.recipients.some(
            item => item.toLowerCase() === normalized
          )
        ) {
          this.recipients.push(email);
        }
      });

      this.emailError = invalidEmails.length
        ? `Nieprawidłowy adres e-mail: ${invalidEmails.join(", ")}`
        : "";

      this.emailInput = "";
    },

    removeRecipient(index) {
      this.recipients.splice(index, 1);
      this.emailError = "";
    },

    participantKey(person) {
      return person.user_id
        ? `user-${person.user_id}`
        : `manual-${person.email || person.name}`;
    },

    participantList(type) {
      return type === "required"
        ? this.requiredParticipants
        : this.optionalParticipants;
    },

    filteredUsers(type) {
      const query = this.participantSearch[type]
        .trim()
        .toLocaleLowerCase("pl");

      if (!query) {
        return [];
      }

      const selected = this.participantList(type);

      return this.availableUsers
        .filter(user => {
          const name = `${user.first_name || ""} ${user.last_name || ""}`
            .toLocaleLowerCase("pl");

          const reversedName = `${user.last_name || ""} ${user.first_name || ""}`
            .toLocaleLowerCase("pl");

          const email = String(user.email || "").toLocaleLowerCase("pl");

          const matches =
            name.includes(query) ||
            reversedName.includes(query) ||
            email.includes(query);

          const alreadySelected = selected.some(person =>
            person.user_id
              ? String(person.user_id) === String(user.id)
              : person.email &&
              user.email &&
              person.email.toLowerCase() === user.email.toLowerCase()
          );

          return matches && !alreadySelected;
        })
        .slice(0, 10);
    },

    hasExactParticipantMatch(type) {
      const query = this.participantSearch[type].trim().toLowerCase();

      if (!query) {
        return false;
      }

      return this.availableUsers.some(user => {
        const fullName =
          `${user.first_name || ""} ${user.last_name || ""}`
            .trim()
            .toLowerCase();

        const reversedName =
          `${user.last_name || ""} ${user.first_name || ""}`
            .trim()
            .toLowerCase();

        const email = String(user.email || "").toLowerCase();

        return fullName === query || reversedName === query || email === query;
      });
    },

    addParticipant(type, user = null) {
      const input = this.participantSearch[type].trim();

      if (!user && !input) {
        return;
      }

      let person;

      if (user) {
        person = {
          user_id: user.id,
          name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
          email: user.email || ""
        };
      } else {
        const email = this.isValidEmail(input) ? input : "";

        person = {
          user_id: null,
          name: email ? email : input,
          email
        };
      }

      const target = this.participantList(type);

      const duplicate = target.some(existing => {
        if (person.user_id && existing.user_id) {
          return String(person.user_id) === String(existing.user_id);
        }

        if (person.email && existing.email) {
          return person.email.toLowerCase() === existing.email.toLowerCase();
        }

        return existing.name.toLowerCase() === person.name.toLowerCase();
      });

      if (!duplicate) {
        target.push(person);
      }

      this.participantSearch[type] = "";
      this.activeParticipantSearch = type;
    },

    removeParticipant(type, index) {
      this.participantList(type).splice(index, 1);
    },

    formatDate(value) {
      if (!value) {
        return "";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return "";
      }

      return date.toLocaleDateString("pl-PL");
    },

    show() {
      this.modalObj?.show();
    },

    hide() {
      this.modalObj?.hide();
    },

    onModalHidden() {
      this.$emit("close");
    },

    async sendNote() {
      this.addRecipient();

      if (
        this.recipients.length === 0 ||
        this.selectedNoteRows.length === 0 ||
        !this.subject.trim() ||
        this.emailError
      ) {
        return;
      }

      try {
        const response = await axios.post(
          route(
            "projects.show.project-file.send-note",
            this.project.id ?? this.project
          ),
          {
            recipients: this.recipients,
			meetingSummary: this.meetingSummary,
            subject: this.subject,
            selectedNoteRows: this.selectedNoteRows,
            requiredParticipants: this.requiredParticipants,
            optionalParticipants: this.optionalParticipants,
            meetingDateTime: this.meetingDateTime,
            meetingLocation: this.meetingLocation
          }
        );

        if (response.data.success) {
          this.$emit("sent", response.data);
          this.hide();
        }
      } catch (error) {
        const validationErrors = error.response?.data?.errors;

        this.emailError =
          error.response?.data?.message ||
          (validationErrors
            ? Object.values(validationErrors).flat().join(" ")
            : "Nie udało się wysłać notatki.");
      }
    }
  }
};
</script>

<style scoped>
.email-tags-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 6px 8px;
  cursor: text;
}

.email-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 9px;
  border-radius: 14px;
  background: #e8f0fe;
  color: #234b87;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.email-tag-remove {
  border: 0;
  padding: 0;
  background: transparent;
  color: #234b87;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.email-tag-input {
  flex: 1;
  min-width: 220px;
  border: 0;
  outline: 0;
  background: transparent;
  padding: 4px;
}

.note-preview {
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  background: #fff;
}

.participant-select {
  position: relative;
  width: 100%;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;
}

.participant-select:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.15);
}

.participant-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 6px 8px;
}

.participant-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 5px 8px;
  border-radius: 5px;
  background: #e9ecef;
  color: #212529;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.participant-tag small {
  color: #6c757d;
}

.participant-tag button {
  border: 0;
  padding: 0 2px;
  background: transparent;
  color: #495057;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.participant-search {
  flex: 1 1 220px;
  width: 100%;
  min-width: 180px;
  padding: 6px 4px;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 14px;
}

.participant-dropdown {
  position: absolute;
  z-index: 1080;
  top: calc(100% + 2px);
  right: 0;
  left: 0;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.participant-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  padding: 9px 12px;
  border: 0;
  border-bottom: 1px solid #f1f3f5;
  background: #fff;
  color: #212529;
  text-align: left;
  cursor: pointer;
}

.participant-option:hover,
.participant-option:focus {
  background: #f1f5f9;
}

.participant-option strong {
  font-size: 14px;
  font-weight: 500;
}

.participant-option small {
  margin-top: 2px;
  color: #6c757d;
  font-size: 12px;
}

.participant-manual {
  background: #f8f9fa;
}

.participant-empty {
  padding: 10px 12px;
  color: #6c757d;
  font-size: 13px;
}

@media (max-width: 576px) {
  .participant-search {
    min-width: 100%;
  }

  .note-preview {
    padding: 6px;
  }
}
</style>
